import React, { useState, useEffect } from "react";
import { Clock, Search, FileText, Wallet, Calendar, CheckCircle, AlertCircle, XCircle, Image as ImageIcon } from "lucide-react";
import "./PendingApproval.css";
import { fetchPendingApprovals, approveRejectPayment } from "../../services/invoiceService";

const PendingApproval = () => {
    const [approvals, setApprovals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [imageConfig, setImageConfig] = useState({ show: false, url: "", title: "" });
    const [rejectModal, setRejectModal] = useState({ show: false, paymentId: null, reason: "" });
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "error" });

    useEffect(() => {
        loadApprovals();
    }, []);

    const loadApprovals = async () => {
        try {
            setIsLoading(true);
            const data = await fetchPendingApprovals();
            setApprovals(data || []);
        } catch (err) {
            console.error("Failed to load pending approvals", err);
            setPopup({
                show: true,
                title: "Error",
                message: "Failed to load pending approvals.",
                type: "error"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleApprove = async (paymentId) => {
        try {
            setIsActionLoading(true);
            await approveRejectPayment({
                PaymentId: paymentId,
                IsApproved: 1,
                RejectionReason: ""
            });
            setPopup({ show: true, title: "Success", message: "Payment approved successfully.", type: "success" });
            loadApprovals();
        } catch (err) {
            console.error("Failed to approve payment", err);
            setPopup({ show: true, title: "Error", message: "Failed to approve payment.", type: "error" });
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleRejectSubmit = async () => {
        if (!rejectModal.reason.trim()) {
            setPopup({ show: true, title: "Validation Error", message: "Rejection reason is mandatory.", type: "error" });
            return;
        }
        try {
            setIsActionLoading(true);
            await approveRejectPayment({
                PaymentId: rejectModal.paymentId,
                IsApproved: 0,
                RejectionReason: rejectModal.reason
            });
            setRejectModal({ show: false, paymentId: null, reason: "" });
            setPopup({ show: true, title: "Success", message: "Payment rejected successfully.", type: "success" });
            loadApprovals();
        } catch (err) {
            console.error("Failed to reject payment", err);
            setPopup({ show: true, title: "Error", message: "Failed to reject payment.", type: "error" });
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleViewProof = (approval) => {
        if (!approval.ProofOfPaidImage) {
            setPopup({
                show: true,
                title: "No Proof",
                message: "No proof image attached to this payment.",
                type: "error"
            });
            return;
        }
        setImageConfig({
            show: true,
            url: approval.ProofOfPaidImage,
            title: `Proof of Payment - ${approval.InvoiceNumber}`
        });
    };

    const filteredApprovals = approvals.filter(item => 
        item.InvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.PaymentMode?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="pending-approval-container">
            <div className="pending-approval-header">
                <h2>Pending Approvals</h2>
                <div className="approval-search">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by invoice # or mode..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="approval-content-area">
                {isLoading && (
                    <div className="approval-loader">
                        <div className="spinner"></div>
                        <p>Loading Pending Approvals...</p>
                    </div>
                )}

                {!isLoading && filteredApprovals.length === 0 && (
                    <div className="empty-state">
                        <CheckCircle size={48} />
                        <h3>All Caught Up!</h3>
                        <p>There are no pending approvals right now.</p>
                    </div>
                )}

                <div className="approval-cards-grid">
                    {filteredApprovals.map((item, index) => (
                        <div className="approval-premium-card" key={`${item.PaymentId}-${index}`}>
                            <div className="card-top-strip">
                                <div className="inv-badge">
                                    <FileText size={14} />
                                    <span>{item.InvoiceNumber}</span>
                                </div>
                                <div className="status-pill pending">
                                    <Clock size={14} />
                                    <span>{item.Status.replace('_', ' ')}</span>
                                </div>
                            </div>

                            <div className="card-body-content">
                                <div className="details-wrap">
                                    <div className="header-row">
                                        <div className="amount-text">
                                            <span className="currency">₹</span>
                                            <span className="value">{parseFloat(item.Amount || 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    
                                    <p className="info-line"><Wallet size={14} /> <span>{item.PaymentMode || 'N/A'}</span></p>
                                    <p className="info-line" title="Paid Date"><Calendar size={14} /> <span>Paid on: {item.PaidDate ? new Date(item.PaidDate).toLocaleDateString() : 'N/A'}</span></p>
                                    <p className="info-line" title="Created On"><Clock size={14} /> <span>Requested: {item.CreatedOn ? new Date(item.CreatedOn).toLocaleString() : 'N/A'}</span></p>
                                </div>
                            </div>

                            <div className="card-footer">
                                <button
                                    className="view-btn"
                                    onClick={() => handleViewProof(item)}
                                >
                                    <ImageIcon size={16} />
                                    Proof
                                </button>
                                <button
                                    className="view-btn approve-btn"
                                    onClick={() => handleApprove(item.PaymentId)}
                                    disabled={isActionLoading}
                                >
                                    <CheckCircle size={16} />
                                    Approve
                                </button>
                                <button
                                    className="view-btn reject-btn"
                                    onClick={() => setRejectModal({ show: true, paymentId: item.PaymentId, reason: "" })}
                                    disabled={isActionLoading}
                                >
                                    <XCircle size={16} />
                                    Reject
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* IMAGE PREVIEW MODAL */}
            {imageConfig.show && (
                <div className="image-modal-overlay">
                    <div className="image-modal-container">
                        <div className="image-modal-header">
                            <h3>{imageConfig.title}</h3>
                            <button 
                                className="image-close-btn" 
                                onClick={() => {
                                    setImageConfig({ ...imageConfig, show: false, url: "" });
                                }}
                            >
                                <span>Close</span>
                            </button>
                        </div>
                        <div className="image-viewer-wrap">
                            <img src={imageConfig.url} alt="Proof of Payment" />
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {rejectModal.show && (
                <div className="reject-modal-overlay">
                    <div className="reject-modal-container">
                        <div className="reject-modal-header">
                            <h3>Reject Payment</h3>
                            <button 
                                className="reject-close-btn" 
                                onClick={() => setRejectModal({ show: false, paymentId: null, reason: "" })}
                            >
                                <span>Close</span>
                            </button>
                        </div>
                        <div className="reject-modal-body">
                            <label>Reason for Rejection <span className="text-danger" style={{ color: "red" }}>*</span></label>
                            <textarea
                                value={rejectModal.reason}
                                onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                                placeholder="Enter rejection reason here..."
                                rows="4"
                            />
                        </div>
                        <div className="reject-modal-footer">
                            <button className="btn-cancel" onClick={() => setRejectModal({ show: false, paymentId: null, reason: "" })}>Cancel</button>
                            <button className="btn-confirm-reject" onClick={handleRejectSubmit} disabled={isActionLoading}>Submit Rejection</button>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP OVERLAY */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={e => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingApproval;
